import React, { useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import useShiftConfiguration from '../../../hooks/configurations/useShiftConfiguration';
import { useTranslation } from 'react-i18next';

type OperationalDayBannerProps = {
  storeNumber: string | undefined;
  franchiseName: string | undefined;
};

const OperationalDayBanner = ({ storeNumber, franchiseName }: OperationalDayBannerProps) => {
  const {
    startOperationalDay,
    endOperationalDay,
    isShiftEnabled,
    storeTimeZone,
    loadOperationalDay,
  } = useShiftConfiguration();

  const { t } = useTranslation('common');
  const theme = useTheme();

  useEffect(() => {
    if (storeNumber && franchiseName) {
      loadOperationalDay(storeNumber, franchiseName);
    }
  }, [storeNumber, franchiseName]);

  if (!isShiftEnabled || !startOperationalDay || !endOperationalDay || !storeTimeZone) {
    return null;
  }

  const current = dayjs().tz(storeTimeZone);
  const operational = dayjs(startOperationalDay).tz(storeTimeZone);
  const end = dayjs(endOperationalDay).tz(storeTimeZone);

  const calendarDateStr = current.format('MM-DD-YYYY');
  const operationalDateStr = operational.format('MM-DD-YYYY');
  const startTime = operational.format('HH:mm:ss');
  const endTime = end.format('HH:mm:ss');

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: theme.palette.common.white,
        pt: { xs: 6, sm: 9 },
        pb: 7,
        pl: { xs: 6, sm: 9 },
        pr: 6,
        borderRadius: { xs: '0px', sm: '40px 0px 0px 0px' },
      }}
    >
      <Box
        sx={{
          color: theme.palette.common.black,
          display: 'flex',
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          gap: '10px 8px',
          alignSelf: 'stretch',
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            minWidth: '220px',
            p: 0,
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            flex: '1 0 0',
          }}
        >
          <Typography
            sx={{
              color: theme.palette.custom.gray[900],
              fontStyle: 'normal',
              fontWeight: 600,
              textAlign: 'left',
            }}
          >
            <strong>{t('shiftingConfiguration.currentDate')}:</strong> {calendarDateStr} <br />
            <strong>{t('shiftingConfiguration.operationalDay')}:</strong> {operationalDateStr}{' '}
            <Box component="span"
              sx={{ color: theme.palette.primary.main }}>
              ({startTime} – {endTime})
            </Box>
          </Typography>
        </Box>

      </Box>
    </Box>
  );
};

export default OperationalDayBanner;
